import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowRight, Sparkles, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const MOCK_LABELS = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00'];

function isMarketOpen() {
  const now = new Date();
  const jakarta = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
  const day = jakarta.getDay();
  if (day === 0 || day === 6) return false;
  const hours = jakarta.getHours();
  const minutes = jakarta.getMinutes();
  const totalMinutes = hours * 60 + minutes;
  return (totalMinutes >= 540 && totalMinutes < 690) || (totalMinutes >= 810 && totalMinutes < 900);
}

function formatLastTradingDay() {
  const now = new Date();
  const jakarta = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
  const day = jakarta.getDay();
  const totalMinutes = jakarta.getHours() * 60 + jakarta.getMinutes();
  let offset = 0;
  if (day === 0) offset = 2;
  else if (day === 6) offset = 1;
  else if (totalMinutes < 540) offset = 1;
  const lastTrading = new Date(jakarta);
  lastTrading.setDate(jakarta.getDate() - offset);
  return lastTrading.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatWIB(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('id-ID', { timeZone: 'Asia/Jakarta', hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatWIBDate() {
  const d = new Date();
  return d.toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatWIBNow() {
  const d = new Date();
  return d.toLocaleTimeString('id-ID', { timeZone: 'Asia/Jakarta', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function generateMockData(seed) {
  const base = seed || 7250 + Math.random() * 150;
  const points = MOCK_LABELS.map((_, i) => {
    const t = i / (MOCK_LABELS.length - 1);
    const trend = (Math.random() - 0.45) * 80 * t;
    const noise = (Math.random() - 0.5) * 40;
    return base + trend + noise;
  });
  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const change = last - prev;
  const changePercent = (change / prev) * 100;
  return {
    labels: MOCK_LABELS,
    values: points.map(v => Math.round(v * 100) / 100),
    current: Math.round(last * 100) / 100,
    change: Math.round(change * 100) / 100,
    changePercent: Math.round(changePercent * 100) / 100,
    high: Math.round(Math.max(...points) * 100) / 100,
    low: Math.round(Math.min(...points) * 100) / 100,
    volume: Math.round(6000000 + Math.random() * 8000000),
    timestamp: Date.now(),
  };
}

function useLiveIHSG() {
  const [data, setData] = useState(() => generateMockData());
  const [dataSource, setDataSource] = useState('loading');
  const marketOpen = useRef(isMarketOpen());

  const parseYahooResponse = useCallback((json) => {
    const result = json?.chart?.result?.[0];
    if (!result) return null;
    const quotes = result.indicators?.quote?.[0];
    const timestamps = result.timestamp || [];
    const closes = quotes?.close || [];
    const valid = closes.map((v, i) => (v !== null ? { v, t: timestamps[i] } : null)).filter(Boolean);
    if (valid.length < 3) return null;
    const nowJakarta = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
    const todayStart = new Date(nowJakarta.getFullYear(), nowJakarta.getMonth(), nowJakarta.getDate());
    const todayStartTs = Math.floor(todayStart.getTime() / 1000);
    const todayData = valid.filter(d => d.t >= todayStartTs);
    const displayData = todayData.length >= 3 ? todayData : valid;
    const values = displayData.map(d => Math.round(d.v * 100) / 100);
    const labels = displayData.map(d => formatWIB(d.t));
    const last = values[values.length - 1];
    const prev = values[values.length - 2];
    const change = last - prev;
    const changePercent = (change / prev) * 100;
    const volume = (result.indicators?.quote?.[0]?.volume || [])
      .filter(v => v !== null)
      .reduce((a, b) => a + b, 0) || 0;
    return {
      labels,
      values,
      current: Math.round(last * 100) / 100,
      change: Math.round(change * 100) / 100,
      changePercent: Math.round(changePercent * 100) / 100,
      high: Math.round(Math.max(...values) * 100) / 100,
      low: Math.round(Math.min(...values) * 100) / 100,
      volume,
      timestamp: Date.now(),
    };
  }, []);

  const fetchIHSG = useCallback(async () => {
    marketOpen.current = isMarketOpen();
    setDataSource('loading');

    const doFetch = async (url) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);
        if (!res.ok) return null;
        const json = await res.json();
        return parseYahooResponse(json);
      } catch {
        clearTimeout(timeout);
        return null;
      }
    };

    const range = marketOpen.current ? '1d' : '5d';
    const interval = marketOpen.current ? '1m' : '5m';

    const endpoints = [
      `https://query1.finance.yahoo.com/v8/finance/chart/%5EJKSE?interval=${interval}&range=${range}&includePrePost=false`,
      `https://query2.finance.yahoo.com/v8/finance/chart/%5EJKSE?interval=${interval}&range=${range}&includePrePost=false`,
      `https://query1.finance.yahoo.com/v8/finance/chart/^JKSE?interval=${interval}&range=${range}&includePrePost=false`,
    ];

    for (const url of endpoints) {
      const result = await doFetch(url);
      if (result) {
        setData(result);
        setDataSource('yahoo');
        return;
      }
    }

    setData(prev => generateMockData(prev?.current));
    setDataSource('simulasi');
  }, [parseYahooResponse]);

  useEffect(() => {
    fetchIHSG();

    const refetchInterval = setInterval(fetchIHSG, 60000);

    if (!marketOpen.current) return () => clearInterval(refetchInterval);

    const tickInterval = setInterval(() => {
      setData(prev => {
        const fluctuation = (Math.random() - 0.5) * 3;
        const newCurrent = prev.current + fluctuation;
        const newValues = [...prev.values.slice(1), Math.round(newCurrent * 100) / 100];
        const newChange = newCurrent - prev.current;
        const newChangePercent = (newChange / prev.current) * 100;
        const newLabel = formatWIBNow();
        const newLabels = [...prev.labels.slice(1), newLabel];
        return {
          ...prev,
          labels: newLabels,
          values: newValues,
          current: Math.round(newCurrent * 100) / 100,
          change: Math.round(newChange * 100) / 100,
          changePercent: Math.round(newChangePercent * 100) / 100,
          high: Math.max(prev.high, newCurrent),
          low: Math.min(prev.low, newCurrent),
          timestamp: Date.now(),
        };
      });
    }, 4000);

    return () => {
      clearInterval(refetchInterval);
      clearInterval(tickInterval);
    };
  }, [fetchIHSG]);

  return { ...data, dataSource, marketOpen: marketOpen.current };
}

function useTheme() {
  const [isDark, setIsDark] = useState(() => document.body.classList.contains('is_dark'));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.body.classList.contains('is_dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

const platforms = [
  {
    key: 'android',
    imgSrc: '/playstore.webp',
    imgAlt: 'Get it on Google Play',
    link: '#',
  },
  {
    key: 'ios',
    imgSrc: '/appstore.webp',
    imgAlt: 'Download on the App Store',
    link: '#',
  },
];

function TradingChartSVG({ w, h, isDark }) {
  const candles = useMemo(() => {
    const candles = [];
    let price = 6800 + Math.random() * 400;
    for (let i = 0; i < 22; i++) {
      const open = price;
      const range = 20 + Math.random() * 60;
      const close = open + (Math.random() - 0.42) * range;
      const high = Math.max(open, close) + Math.random() * range * 0.6;
      const low = Math.min(open, close) - Math.random() * range * 0.6;
      const volume = 3000 + Math.random() * 12000;
      candles.push({ open, high, low, close, volume });
      price = close;
    }
    return candles;
  }, []);
  const pad = { top: 8, right: 14, bottom: 30, left: 28 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const candleW = chartW / candles.length;
  const bodyW = Math.max(candleW * 0.55, 2.5);
  const gridColor = isDark ? '#ffffff' : '#0f172a';
  const fgOpacityScale = isDark ? 0.13 : 0.1;

  const allPrices = candles.flatMap(c => [c.high, c.low]);
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice || 1;
  const scaleY = (v) => pad.top + chartH - ((v - minPrice) / priceRange) * chartH;

  const maxVol = Math.max(...candles.map(c => c.volume));
  const volH = chartH * 0.18;

  const ma7 = [];
  const ma25 = [];
  for (let i = 0; i < candles.length; i++) {
    const slice7 = candles.slice(Math.max(0, i - 6), i + 1);
    const slice25 = candles.slice(Math.max(0, i - 24), i + 1);
    ma7.push(slice7.reduce((s, c) => s + c.close, 0) / slice7.length);
    ma25.push(slice25.reduce((s, c) => s + c.close, 0) / slice25.length);
  }

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      preserveAspectRatio="xMidYMid slice"
    >
      {/* Grid */}
      {[0, 1, 2, 3].map(i => {
        const y = pad.top + (chartH / 3) * i;
        return (
          <line key={`g${i}`} x1={pad.left} y1={y} x2={w - pad.right} y2={y}
            stroke={gridColor} strokeOpacity={fgOpacityScale} strokeWidth="0.5" strokeDasharray="3,3" />
        );
      })}

      {/* Volume Bars */}
      {candles.map((c, i) => {
        const x = pad.left + i * candleW + candleW * 0.1;
        const barH = (c.volume / maxVol) * volH;
        const barY = h - pad.bottom + volH - barH;
        return (
          <rect key={`vol-${i}`} x={x} y={barY} width={bodyW} height={Math.max(barH, 0.5)}
            fill={c.close >= c.open ? '#58bd7d' : '#ef4444'} opacity={fgOpacityScale * 1.8} rx="1" />
        );
      })}

      {/* MA25 Line */}
      <polyline
        points={ma25.map((v, i) => `${pad.left + i * candleW + candleW / 2},${scaleY(v)}`).join(' ')}
        fill="none" stroke="#f59e0b" strokeWidth="1" opacity={fgOpacityScale * 3.5}
        strokeLinecap="round" strokeLinejoin="round"
      />

      {/* MA7 Line */}
      <polyline
        points={ma7.map((v, i) => `${pad.left + i * candleW + candleW / 2},${scaleY(v)}`).join(' ')}
        fill="none" stroke="#8b5cf6" strokeWidth="1" opacity={fgOpacityScale * 3}
        strokeLinecap="round" strokeLinejoin="round"
      />

      {/* Candlesticks */}
      {candles.map((c, i) => {
        const x = pad.left + i * candleW + candleW / 2;
        const isUp = c.close >= c.open;
        const color = isUp ? '#58bd7d' : '#ef4444';
        const yOpen = scaleY(c.open);
        const yClose = scaleY(c.close);
        const yHigh = scaleY(c.high);
        const yLow = scaleY(c.low);
        const bodyOpacity = fgOpacityScale * 4.5;
        return (
          <g key={`candle-${i}`}>
            <line x1={x} y1={yHigh} x2={x} y2={yLow} stroke={color} strokeWidth="0.8" opacity={bodyOpacity * 0.8} />
            <rect
              x={x - bodyW / 2}
              y={Math.min(yOpen, yClose)}
              width={bodyW}
              height={Math.max(Math.abs(yClose - yOpen), 0.5)}
              fill={color}
              opacity={bodyOpacity}
              rx="0.8"
            />
          </g>
        );
      })}
    </svg>
  );
}

function HeroTradioraSection() {
  const ihsgData = useLiveIHSG();
  const isDark = useTheme();
  const chartRef = useRef(null);

  const chartData = {
    labels: ihsgData.labels,
    datasets: [
      {
        data: ihsgData.values,
        borderColor: '#0C8F86',
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: '#0C8F86',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        tension: 0.35,
        fill: true,
        backgroundColor: (ctx) => {
          const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, ctx.chart.height);
          gradient.addColorStop(0, 'rgba(12,143,134,0.25)');
          gradient.addColorStop(0.5, 'rgba(12,143,134,0.08)');
          gradient.addColorStop(1, 'rgba(12,143,134,0.01)');
          return gradient;
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500, easing: 'easeOutQuart' },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      tooltip: {
        backgroundColor: isDark ? 'rgba(30,41,59,0.95)' : 'rgba(255,255,255,0.95)',
        titleColor: isDark ? '#e2e8f0' : '#1e293b',
        bodyColor: '#0C8F86',
        borderColor: 'rgba(12,143,134,0.25)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => `IHSG: ${ctx.parsed.y.toLocaleString('id-ID')}`,
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          font: { size: 9, family: 'DM Sans' },
          maxTicksLimit: 6,
          autoSkip: true,
        },
      },
      y: {
        display: true,
        position: 'right',
        grid: {
          color: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
          drawBorder: false,
        },
        ticks: {
          color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          font: { size: 9, family: 'DM Sans' },
          maxTicksLimit: 6,
          callback: (val) => val.toLocaleString('id-ID'),
        },
      },
    },
  };

  const isUp = ihsgData.changePercent >= 0;
  const marketOpen = ihsgData.marketOpen;

  const theme = {
    bgGradient: isDark
      ? 'linear-gradient(165deg, #080f1e 0%, #0b1a2e 25%, #0f162a 50%, #130d26 75%, #080f1e 100%)'
      : 'linear-gradient(165deg, #f8fafc 0%, #f1f5f9 25%, #eff6ff 50%, #f5f3ff 75%, #f8fafc 100%)',
    orbColor1: isDark ? 'rgba(12,143,134,0.08)' : 'rgba(12,143,134,0.1)',
    orbColor2: isDark ? 'rgba(139,92,246,0.07)' : 'rgba(139,92,246,0.08)',
    orbColor3: isDark ? 'rgba(12,143,134,0.04)' : 'rgba(12,143,134,0.06)',
    gridColor: isDark ? 'rgba(12,143,134,0.06)' : 'rgba(12,143,134,0.08)',
    textPrimary: isDark ? '#fff' : '#0f172a',
    textSecondary: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)',
    textTertiary: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.35)',
    badgeBg: isDark ? 'rgba(12,143,134,0.12)' : 'rgba(12,143,134,0.08)',
    badgeBorder: isDark ? 'rgba(12,143,134,0.15)' : 'rgba(12,143,134,0.12)',
    badgeText: isDark ? 'rgba(255,255,255,0.85)' : '#0f172a',
    outlineBtnBg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    outlineBtnBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    outlineBtnText: isDark ? '#fff' : '#0f172a',
    outlineBtnHoverBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    chartCardBg: isDark ? 'rgba(15,23,42,0.75)' : 'rgba(255,255,255,0.85)',
    chartCardBorder: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
    platformCardBg: isDark ? 'rgba(10,15,28,0.65)' : 'rgba(255,255,255,0.75)',
    platformCardBorder: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
  };

  return (
    <section
      style={{
        background: theme.bgGradient,
        padding: '120px 0 80px',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        transition: 'background 0.5s ease',
      }}
    >
      <style>{`
        @keyframes heroOrbFloat1 {
          0%, 100% { transform: scale(1) translate(0, 0); opacity: 0.7; }
          33% { transform: scale(1.08) translate(25px, -15px); opacity: 1; }
          66% { transform: scale(0.93) translate(-15px, 12px); opacity: 0.45; }
        }
        @keyframes heroOrbFloat2 {
          0%, 100% { transform: scale(1) translate(0, 0); opacity: 0.6; }
          33% { transform: scale(0.92) translate(-20px, 12px); opacity: 0.35; }
          66% { transform: scale(1.06) translate(18px, -12px); opacity: 0.8; }
        }
        @keyframes heroGlowPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.14); opacity: 0.55; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.7); }
        }
        @keyframes float3d {
          0%, 100% { transform: translateY(0px) rotateX(0deg) rotateY(0deg); }
          25% { transform: translateY(-6px) rotateX(2deg) rotateY(2deg); }
          75% { transform: translateY(4px) rotateX(-1deg) rotateY(-1deg); }
        }
        @keyframes tickerFlash {
          0% { background-color: rgba(12,143,134,0); }
          50% { background-color: rgba(12,143,134,0.08); }
          100% { background-color: rgba(12,143,134,0); }
        }
        @keyframes tickerFlashRed {
          0% { background-color: rgba(239,68,68,0); }
          50% { background-color: rgba(239,68,68,0.08); }
          100% { background-color: rgba(239,68,68,0); }
        }
        @keyframes chartFloat1 {
          0%, 100% { transform: translateY(0) translateX(0) scale(1); }
          33% { transform: translateY(-15px) translateX(8px) scale(1.03); }
          66% { transform: translateY(10px) translateX(-5px) scale(0.97); }
        }
        @keyframes chartFloat2 {
          0%, 100% { transform: translateY(0) translateX(0) scale(1); }
          40% { transform: translateY(12px) translateX(-10px) scale(0.98); }
          75% { transform: translateY(-18px) translateX(6px) scale(1.04); }
        }
        @keyframes chartFloat3 {
          0%, 100% { transform: translateY(0) translateX(0) scale(1); }
          25% { transform: translateY(-22px) translateX(-8px) scale(1.02); }
          55% { transform: translateY(15px) translateX(10px) scale(0.96); }
          85% { transform: translateY(-8px) translateX(-3px) scale(1.01); }
        }
      `}</style>

      {/* Ambient glow orbs */}
      <div style={{
        position: 'absolute', top: '-20%', left: '-10%', width: 800, height: 800,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.orbColor1} 0%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'heroOrbFloat1 12s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', bottom: '-25%', right: '-10%', width: 900, height: 900,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.orbColor2} 0%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'heroOrbFloat2 14s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', top: '35%', left: '50%', width: 500, height: 500,
        borderRadius: '50%', transform: 'translate(-50%, -50%)',
        background: `radial-gradient(circle, ${theme.orbColor3} 0%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'heroGlowPulse 6s ease-in-out infinite',
      }} />

      {/* Grid pattern */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `radial-gradient(${theme.gridColor} 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
        opacity: 0.5,
      }} />

      {/* Floating decorative shapes */}
      <div style={{
        position: 'absolute', top: '18%', left: '4%',
        width: 56, height: 56,
        border: `2px solid ${isDark ? 'rgba(12,143,134,0.08)' : 'rgba(12,143,134,0.1)'}`,
        borderRadius: 16, transform: 'rotate(30deg)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: '30%', right: '7%',
        width: 36, height: 36,
        border: `2px solid ${isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.12)'}`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      {/* Trading Chart SVG Backgrounds */}
      <div style={{
        position: 'absolute', left: '2%', top: '8%',
        animation: 'chartFloat1 14s ease-in-out infinite',
        pointerEvents: 'none', zIndex: 0,
        opacity: isDark ? 0.1 : 0.07,
        transition: 'opacity 0.5s ease',
      }}>
        <TradingChartSVG w={320} h={200} isDark={isDark} />
      </div>
      <div style={{
        position: 'absolute', right: '2%', top: '52%',
        animation: 'chartFloat2 16s ease-in-out infinite',
        pointerEvents: 'none', zIndex: 0,
        opacity: isDark ? 0.09 : 0.06,
        transition: 'opacity 0.5s ease',
      }}>
        <TradingChartSVG w={280} h={180} isDark={isDark} />
      </div>
      <div style={{
        position: 'absolute', left: '48%', top: '4%',
        animation: 'chartFloat3 15s ease-in-out 2s infinite',
        pointerEvents: 'none', zIndex: 0,
        opacity: isDark ? 0.08 : 0.05,
        transition: 'opacity 0.5s ease',
      }}>
        <TradingChartSVG w={260} h={170} isDark={isDark} />
      </div>

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="row align-items-center">
          {/* Left Column - Content */}
          <div className="col-xl-7 col-lg-6 col-md-12" data-aos="fade-right" data-aos-duration="1000">
            <div style={{ maxWidth: 600 }}>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{
                  fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800,
                  color: theme.textPrimary,
                  lineHeight: 1.1, marginBottom: 16, letterSpacing: '-1.5px',
                }}
              >
                Trading Lebih Cerdas
                <br />
                dengan{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #0C8F86 0%, #58bd7d 50%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 6s ease infinite',
                }}>
                  Tradiora
                </span>
              </motion.h1>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                style={{
                  fontSize: 'clamp(15px, 1.8vw, 17px)', color: theme.textSecondary,
                  lineHeight: 1.75, marginBottom: 24, maxWidth: 500,
                }}
              >
                Platform all-in-one untuk analisis pasar real-time, mentoring profesional,
                dan komunitas trader aktif. Tersedia di Android dan iOS — trading
                kapan saja, di mana saja.
              </motion.p>

              {/* Platform Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{
                  display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28,
                }}
              >
                {platforms.map((p) => (
                  <a
                    key={p.key}
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      background: theme.platformCardBg,
                      backdropFilter: 'blur(12px)',
                      borderRadius: 14, padding: '8px 16px',
                      border: `1px solid ${theme.platformCardBorder}`,
                      textDecoration: 'none', cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = isDark
                        ? '0 10px 30px rgba(0,0,0,0.4)'
                        : '0 10px 30px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = 'var(--primary)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.borderColor = theme.platformCardBorder;
                    }}
                  >
                    <img src={p.imgSrc} alt={p.imgAlt} style={{ height: 42, display: 'block' }} />
                  </a>
                ))}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}
              >
                <Link
                  to="/register"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #0C8F86, #087a72)',
                    color: '#fff', fontWeight: 700, fontSize: 15,
                    borderRadius: 14, padding: '15px 32px', textDecoration: 'none',
                    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    boxShadow: '0 10px 35px rgba(12,143,134,0.35)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 14px 45px rgba(12,143,134,0.45)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 10px 35px rgba(12,143,134,0.35)';
                  }}
                >
                  <TrendingUp size={20} />
                  Mulai Trading Sekarang
                </Link>
                <Link
                  to="/features"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: theme.outlineBtnBg, color: theme.outlineBtnText,
                    fontWeight: 600, fontSize: 15, borderRadius: 14,
                    padding: '15px 28px', textDecoration: 'none',
                    border: `1px solid ${theme.outlineBtnBorder}`,
                    backdropFilter: 'blur(10px)', transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = theme.outlineBtnHoverBg;
                    e.currentTarget.style.borderColor = 'var(--primary)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = theme.outlineBtnBg;
                    e.currentTarget.style.borderColor = theme.outlineBtnBorder;
                  }}
                >
                  Pelajari Fitur
                  <ArrowRight size={18} />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Right Column - Live IHSG Chart ala TradingView */}
          <div className="col-xl-5 col-lg-6 col-md-12" data-aos="fade-left" data-aos-duration="1000" data-aos-delay="200">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                paddingTop: 20, position: 'relative',
              }}
            >
              {/* Glow behind chart card */}
              <div style={{
                position: 'absolute', width: 420, height: 420,
                borderRadius: '50%',
                background: isDark
                  ? 'radial-gradient(circle, rgba(12,143,134,0.12) 0%, rgba(139,92,246,0.06) 50%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(12,143,134,0.08) 0%, rgba(139,92,246,0.04) 50%, transparent 70%)',
                pointerEvents: 'none',
                animation: 'heroGlowPulse 5s ease-in-out infinite',
              }} />

              {/* Chart Card */}
              <div style={{
                position: 'relative',
                width: '100%', maxWidth: 480,
                background: theme.chartCardBg,
                backdropFilter: 'blur(20px)',
                borderRadius: 28,
                padding: 0,
                border: `1px solid ${theme.chartCardBorder}`,
                boxShadow: isDark
                  ? '0 30px 70px rgba(0,0,0,0.4), 0 10px 25px rgba(12,143,134,0.1)'
                  : '0 20px 50px rgba(0,0,0,0.06), 0 5px 15px rgba(12,143,134,0.05)',
                animation: 'float3d 6s ease-in-out infinite',
                overflow: 'hidden',
              }}>
                {/* Top Bar - TradingView style */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 20px 10px',
                  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'linear-gradient(135deg, #0C8F86, #087a72)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 6px 16px rgba(12,143,134,0.3)',
                    }}>
                      <TrendingUp size={18} color="#fff" />
                    </div>
                    <div>
                      <div style={{
                        color: theme.textPrimary, fontWeight: 700, fontSize: 14,
                        lineHeight: 1.2, letterSpacing: '-0.3px',
                      }}>
                        IHSG
                      </div>
                      <div style={{
                        color: theme.textTertiary, fontSize: 10,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        IDX: COMPOSITE
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: marketOpen
                            ? 'rgba(88,189,125,0.15)'
                            : 'rgba(239,68,68,0.1)',
                          color: marketOpen ? '#58bd7d' : '#ef4444',
                          padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                        }}>
                          <span style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: marketOpen ? '#58bd7d' : '#ef4444',
                            animation: marketOpen ? 'livePulse 1.5s ease-in-out infinite' : 'none',
                          }} />
                          {marketOpen ? 'OPEN' : 'CLOSED'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      color: theme.textPrimary, fontSize: 26, fontWeight: 800,
                      lineHeight: 1, letterSpacing: '-0.5px',
                      fontFamily: 'DM Sans, sans-serif',
                    }}>
                      {ihsgData.current?.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      justifyContent: 'flex-end', marginTop: 3,
                    }}>
                      <motion.span
                        key={ihsgData.changePercent}
                        initial={{ opacity: 0.6 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          color: isUp ? '#58bd7d' : '#ef4444',
                          fontSize: 14, fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: 2,
                        }}
                      >
                        {isUp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {ihsgData.changePercent > 0 ? '+' : ''}{ihsgData.changePercent}%
                      </motion.span>
                      <span style={{
                        color: isUp ? '#58bd7d' : '#ef4444',
                        fontSize: 12, fontWeight: 600,
                      }}>
                        ({ihsgData.change > 0 ? '+' : ''}{ihsgData.change?.toLocaleString('id-ID')})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Chart Area */}
                <div style={{ padding: '12px 12px 8px', height: 220 }}>
                  <Line ref={chartRef} data={chartData} options={chartOptions} />
                </div>

                {/* Bottom Stats - TradingView style bar */}
                <div style={{
                  display: 'flex', alignItems: 'center',
                  padding: '8px 20px 14px',
                  gap: 20,
                  borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`,
                }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: theme.textTertiary, fontSize: 9, marginBottom: 1 }}>HIGH</div>
                    <div style={{ color: '#58bd7d', fontSize: 12, fontWeight: 700 }}>
                      {ihsgData.high?.toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: theme.textTertiary, fontSize: 9, marginBottom: 1 }}>LOW</div>
                    <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 700 }}>
                      {ihsgData.low?.toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: theme.textTertiary, fontSize: 9, marginBottom: 1 }}>VOL</div>
                    <div style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 700 }}>
                      {ihsgData.volume >= 1000000
                        ? (ihsgData.volume / 1000000).toFixed(1) + 'M'
                        : ihsgData.volume >= 1000
                          ? (ihsgData.volume / 1000).toFixed(1) + 'K'
                          : ihsgData.volume}
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ color: theme.textTertiary, fontSize: 9, marginBottom: 1 }}>
                      {marketOpen ? formatWIBDate() : formatLastTradingDay()}
                    </div>
                    <div style={{
                      color: theme.textSecondary, fontSize: 12, fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end',
                    }}>
                      {marketOpen ? (
                        <>WIB {formatWIBNow()} <span style={{ color: ihsgData.dataSource === 'yahoo' ? '#58bd7d' : '#f59e0b', fontSize: 8 }}>· LIVE</span></>
                      ) : (
                        ihsgData.dataSource === 'yahoo'
                          ? <span style={{ color: '#ef4444', fontSize: 10, fontWeight: 600 }}>Data Close</span>
                          : <span style={{ color: '#f59e0b', fontSize: 10, fontWeight: 600 }}>Data Simulasi Close</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroTradioraSection;
